"use client";

import { ICON_SIZES, Icon } from "@/components/library/Icon";
import {
  type ComparisonCell,
  type ComparisonColumn,
  type ComparisonIncludedNote,
  type ComparisonSection,
  type PricingPlanCtaType,
} from "./pricingTypes";
import { Fragment } from "react";

import Link from "next/link";
import clsx from "clsx";
import { useBillingPeriod } from "./MarketingPricingProvider";
import { useDemoButtonLink } from "@/hooks/useDemoButtonText";
import { useLocalePath } from "@/hooks/useLocalePath";
import { withDemoSource } from "@/utils/demoSource";
import { useTrackDemoCtaClick } from "@/hooks/useDemoCtaTracking";

export type MarketingPricingComparisonProps = {
  columns: ComparisonColumn[];
  sections: ComparisonSection[];
  included: ComparisonIncludedNote;
  disclaimer: string[];
  className?: string;
};

function ComparisonCta({
  label,
  ctaType,
  highlighted,
}: Readonly<{
  label: string;
  ctaType: PricingPlanCtaType;
  highlighted?: boolean;
}>) {
  const lp = useLocalePath();
  const demoLink = useDemoButtonLink(lp("/contact-sales"));
  const trackDemoCtaClick = useTrackDemoCtaClick();

  let href = lp("/signup");
  let target: "_blank" | "_self" = "_self";
  let isDemoCta = false;

  if (ctaType === "demo") {
    href =
      demoLink.target === "_self"
        ? withDemoSource(demoLink.href, "pricing_comparison")
        : demoLink.href;
    target = demoLink.target;
    isDemoCta = true;
  } else if (ctaType === "contactSales") {
    href = withDemoSource(lp("/contact-sales"), "pricing_comparison");
    isDemoCta = true;
  }

  return (
    <Link
      href={href}
      target={target}
      onClick={
        isDemoCta
          ? () =>
              trackDemoCtaClick({
                source: "pricing_comparison",
                placement: "pricing_comparison",
              })
          : undefined
      }
      className={clsx(
        "inline-flex w-full items-center justify-center rounded-lg px-4 py-2 font-monument text-sm font-medium transition-colors",
        highlighted
          ? "bg-gail-primary text-white hover:bg-gail-primary-hover"
          : "border border-black/10 bg-white text-black hover:bg-black/[0.03]",
      )}
    >
      {label}
    </Link>
  );
}

function ValueCell({
  cell,
  highlighted,
}: Readonly<{ cell: ComparisonCell; highlighted?: boolean }>) {
  return (
    <div
      className={clsx(
        "flex items-center justify-center border-b border-black/5 px-4 py-3 text-center",
        highlighted && "bg-gail-primary-light",
      )}
    >
      {cell.kind === "check" ? (
        <Icon
          icon="circle-check"
          size={ICON_SIZES.SM}
          className="text-gail-primary"
        />
      ) : cell.kind === "none" ? (
        <span className="text-black/25" aria-hidden>
          –
        </span>
      ) : (
        <span className="font-monument text-sm text-black/70">{cell.text}</span>
      )}
    </div>
  );
}

export function MarketingPricingComparison({
  columns,
  sections,
  included,
  disclaimer,
  className,
}: Readonly<MarketingPricingComparisonProps>) {
  const { billingPeriod } = useBillingPeriod();

  return (
    <section className={clsx("w-full px-4 pb-16", className)}>
      <div className="mx-auto max-w-6xl max-md:overflow-x-auto">
        <div className="grid grid-cols-[minmax(0,1.7fr)_repeat(4,minmax(0,1fr))] rounded-[20px] border border-black/10 bg-marketing-voice-card max-md:min-w-[760px]">
          {/* Header row */}
          <div className="sticky top-0 z-30 rounded-tl-[20px] border-b border-black/10 bg-marketing-voice-card" />
          {columns.map((col, index) => (
            <div
              key={col.id}
              className={clsx(
                "sticky top-0 z-30 flex flex-col items-center border-b border-black/10 px-4 pb-4 pt-5 text-center",
                col.highlighted
                  ? "border-t-2 border-t-gail-primary bg-gail-primary-light"
                  : "bg-marketing-voice-card",
                index === columns.length - 1 && "rounded-tr-[20px]",
              )}
            >
              <span
                className={clsx(
                  "font-monument text-base",
                  col.highlighted ? "text-gail-primary" : "text-black",
                )}
              >
                {col.name}
              </span>
              <span className="mt-1 font-monument text-xs text-black/70">
                {billingPeriod === "yearly"
                  ? col.priceLabelYearly
                  : col.priceLabelMonthly}
              </span>
            </div>
          ))}

          {/* Sections */}
          {sections.map((section) => (
            <Fragment key={section.title}>
              <div className="px-4 pb-3 pt-8 font-monument text-xs font-medium uppercase tracking-wide text-black/70">
                {section.title}
              </div>
              {columns.map((col) => (
                <div
                  key={col.id}
                  className={clsx(col.highlighted && "bg-gail-primary-light")}
                />
              ))}

              {section.rows.map((row) => (
                <Fragment key={row.label}>
                  <div className="flex flex-col justify-center border-b border-black/5 px-4 py-3 font-monument text-sm text-black/70">
                    <span>{row.label}</span>
                    {row.sublabel && (
                      <span className="mt-0.5 text-xs text-black/70">
                        {row.sublabel}
                      </span>
                    )}
                  </div>
                  {columns.map((col, index) => (
                    <ValueCell
                      key={col.id}
                      cell={row.cells[index] ?? { kind: "none" }}
                      highlighted={col.highlighted}
                    />
                  ))}
                </Fragment>
              ))}
            </Fragment>
          ))}

          {/* Footer CTA row */}
          <div />
          {columns.map((col) => (
            <div
              key={col.id}
              className={clsx(
                "px-3 pb-6 pt-6",
                col.highlighted && "bg-gail-primary-light",
              )}
            >
              <ComparisonCta
                label={col.ctaLabel}
                ctaType={col.ctaType}
                highlighted={col.highlighted}
              />
            </div>
          ))}

          {/* Included on every plan row */}
          <div className="flex items-center rounded-bl-[20px] border-t border-black/10 px-4 py-4 font-monument text-sm text-black/70">
            {included.label}
          </div>
          <div className="col-span-4 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 rounded-br-[20px] border-t border-black/10 px-4 py-4">
            {included.items.map((item) => (
              <span
                key={item}
                className="flex items-center gap-2 font-monument text-sm text-black/70"
              >
                <Icon
                  icon="circle-check"
                  size={ICON_SIZES.SM}
                  className="text-gail-primary"
                />
                {item}
              </span>
            ))}
          </div>
        </div>
      </div>

      <div className="mx-auto mt-6 max-w-6xl space-y-1 font-monument text-xs leading-relaxed text-black/70">
        {disclaimer.map((line) => {
          const hasAsterisk = line.startsWith("*");

          return (
            <p key={line}>
              {hasAsterisk ? (
                <>
                  <span className="text-gail-primary">*</span>
                  {line.slice(1)}
                </>
              ) : (
                line
              )}
            </p>
          );
        })}
      </div>
    </section>
  );
}
